<template>
    <div class="mb-16">
        <p
            class="text-overline grey--text text--darken-1 mb-0"
            style="line-height: 1.5;"
        >
            Bulk Message Review
        </p>
        <h1 class="text-h6 font-weight-medium">
            {{ bulk_message.label }}
        </h1>

        <v-card
            v-if="bulk_message.scheduled_send_time"
            class="shaded-light px-4 py-3 mt-3 mb-8 d-flex flex-wrap align-center justify-space-between gap-3"
            style="border-left: 5px solid var(--v-success-base)"
            elevation="0"
        >
            <p class="mb-0">
                <v-icon
                    size="24"
                    color="success"
                    class="mr-1"
                    aria-hidden="true"
                >mdi-calendar-clock</v-icon>
                This message is scheduled to be sent on <strong>
                    {{ scheduled_send_time_display.date }}
                </strong> at or around <strong>
                    {{ scheduled_send_time_display.time }}.
                </strong>
            </p>

            <v-btn 
                color="primary"
                outlined
                small
                @click="onClickCancelScheduledSend"
            >
                <v-icon
                    size="16"
                    aria-hidden="true"
                    left
                >
                    mdi-cancel
                </v-icon>
                Cancel Scheduled Send
            </v-btn>
        </v-card>

        <v-card
            v-else
            class="shaded-light px-4 py-3 mt-3 mb-8 d-flex flex-wrap align-center justify-space-between gap-3"
            style="border-left: 5px solid var(--v-primary-base)"
            elevation="0"
        >
            <p class="mb-0">
                Before sending your message, verify everything looks correct below. If you need to change
                anything outside of removing recipients, edit your message settings.
            </p>

            <v-btn 
                color="primary"
                outlined
                small
                @click="onBackToEdit('Edit Message Settings')"
            >
                <v-icon
                    size="16"
                    aria-hidden="true"
                    left
                >
                    mdi-lead-pencil
                </v-icon>
                Edit Message Settings
            </v-btn>
        </v-card>

        <message-patterns-readonly
            :organization_id="organization_id"
            :bulk_message="bulk_message"
            class="mt-4 mb-5"
        />

        <recipients-tracker
            :organization_id="organization_id"
            :bulk_message="bulk_message"
            :bulk_message_id="bulk_message_id"
            :has_sent="has_sent"
            :message_state="message_state"
            :progress_detail="progress_detail"
            v-on="$listeners"
            @cancel-review="onBackToEdit('Cancel Preparation')"
        />

        <template v-if="message_state === MESSAGE_STATES.REVIEW">
            <recipients-table-review
                :organization_id="organization_id"
                :bulk_message="bulk_message"
                :bulk_message_id="bulk_message_id"
                :message_state="message_state"
                :has_sent="has_sent"
                :loading_recipients="loading_recipients"
                :selected_recipient_filter="selected_recipient_filter"
                :recipients="recipients"
                :recipient_extra_fields="recipient_extra_fields"
                :full_text="full_text"
                :num_recipients_under_current_filter="num_recipients_under_current_filter"
                :progress_detail="progress_detail"
                :table_options.sync="local_table_options"
                class="mb-10"
                v-on="$listeners"
            />

            <div class="d-flex align-center gap-2 mt-12">
                <v-icon
                    size="26"
                    color="primary"
                    style="padding-bottom: 3px"
                    aria-hidden="true"
                >
                    mdi-account-check
                </v-icon>
                <h2>Before Sending to Each Recipient, RedChirp Should ...</h2>
            </div>

            <div class="ml-md-4">
                <v-row
                    v-show="bulk_message.filter_profile.filter_simple_do_not_send_after_wall_time.visible"
                    class="mt-2"
                >
                    <v-col
                        cols="12"
                        md="auto"
                    >
                        <v-switch
                            :key="wall_time_switch_key"
                            :input-value="filter_simple_do_not_send_after_wall_time_boolean"
                            :readonly="has_sent"
                            class="mt-1"
                            hide-details
                            @change="onSwitchSimpleDoNotSendAfterWallTime"
                        >
                            <template #label>
                                <span>
                                    {{ bulk_message.filter_profile.filter_simple_do_not_send_after_wall_time.label }}
                                    <strong class="pl-1">
                                        {{ filter_simple_do_not_send_after_wall_time_display_value }}
                                    </strong>
                                </span>
                            </template>

                            <template #append>
                                <v-tooltip bottom>
                                    <template #activator="{ on, attrs }">
                                        <v-btn
                                            v-bind="attrs"
                                            v-on="on"
                                            icon
                                            small
                                            color="primary"
                                            class="mr-2"
                                            :disabled="has_sent"
                                            aria-label="Edit time"
                                            @click="onEditFilterSimpleDoNotSendAfter"
                                        >
                                            <v-icon
                                                size="24"
                                                class="mb-1"
                                                aria-hidden="true"
                                            >mdi-lead-pencil</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edit time</span>
                                </v-tooltip>
                            </template>
                        </v-switch>
                    </v-col>
                </v-row>

                <v-row class="mt-2">
                    <v-col
                        cols="12"
                        md="auto"
                    >
                        <v-switch
                            v-show="bulk_message.filter_profile.filter_send_time.visible"
                            :label="bulk_message.filter_profile.filter_send_time.label"
                            :input-value="filter_send_time_boolean"
                            class="mt-1"
                            :disabled="has_sent"
                            hide-details
                            @change="onSendTimeFilterEnabledChange"
                        />
                    </v-col>
                </v-row>

                <transition name="fade">
                    <v-row
                        v-show="bulk_message.filter_profile.filter_send_time.visible && filter_send_time_boolean"
                        class="ml-5 mt-1"
                    >
                        <v-col
                            cols="12"
                            sm="10"
                            md="7"
                            lg="6"
                            xl="5"
                            class="pt-0"
                        >
                            <v-select
                                label=""
                                class="ml-6 mb-2"
                                :aria-label="bulk_message.filter_profile.filter_send_time.label"
                                :value="bulk_message.filter_send_time"
                                :items="$rc.enumValues('send_time_filter_type').filter(o => o.value !== 'none')"
                                :disabled="has_sent"
                                :clearable="false"
                                hide-details
                                outlined
                                dense
                                @change="onSendTimeFilterChange"
                            />
                        </v-col>
                    </v-row>
                </transition>

                <rc-link
                    :to="{ name: 'public-restricted-messaging-times' }"
                    text="Click here for more state by state details"
                    text_transform="uppercase"
                    class="mt-4"
                />

                <p class="text-caption text--secondary mt-4 mb-0">
                    <strong>NOTE:</strong> RedChirp tries to check all applicable filters again while sending in
                    case something has changed
                </p>
            </div>

            <template v-if="!is_archived">
                <v-divider class="mt-6" />
    
                <v-row class="my-4">
                    <v-col
                        cols="12"
                        sm="10"
                        md="7"
                        lg="6"
                        xl="4"
                    >
                        <m-edit-select-employee
                            :organization_id="organization_id"
                            :value="bulk_message.notification_employee_ids"
                            label="Users to notify when message is sent (optional)"
                            multiple
                            hide_details
                            dense
                            outlined
                            clearable
                            @change="onSetUsersToNotify"
                        />
                    </v-col>
                </v-row>
    
                <v-divider class="mb-4" />
    
                <template v-if="!is_scheduled_but_unsent">
                    <div data-validation-target="attestation">
                        <m-legal-agreement
                            ref="bulk_message_attestation"
                            :organization_id="organization_id"
                            v-model="consent_acknowledgement"
                            class="mb-3"
                            type="bulk-message-attestation"
                            hide_details
                        />
                    </div>
    
                    <div data-validation-target="tested">
                        <v-checkbox
                            v-model="tested_message"
                            label="I have tested my message and am happy with the message and any attachments"
                            class="mt-1 mr-4"
                            hide-details
                        />
    
                        <p class="mt-1 ml-8">
                            <rc-link
                                href="https://vimeo.com/650475431"
                                text="How to test your messages"
                                open_in_new_tab
                            />
                        </p>
                    </div>
                </template>
    
                <div class="shaded-light pa-4 pb-6 mt-8">
                    <div class="d-flex align-center gap-2">
                        <v-icon
                            size="26"
                            color="primary"
                            style="padding-bottom: 3px"
                            aria-hidden="true"
                        >
                            mdi-calendar-clock
                        </v-icon>
                        <h2>When to Send</h2>
                    </div>
    
                    <v-row
                        class="mt-0 ml-2"
                        align="end"
                    >
                        <v-col
                            cols="12"
                            md="auto"
                        >
                            <v-radio-group
                                v-model="when_to_send"
                                class="mt-0"
                                :disabled="is_scheduled_but_unsent"
                                hide-details
                            >
                                <v-radio
                                    v-for="option in when_to_send_options"
                                    :key="option.value"
                                    :label="option.label"
                                    :value="option.value"
                                    class="mt-1"
                                />
                            </v-radio-group>
                        </v-col>
    
                        <v-col cols="auto">
                            <transition name="fade">
                                <v-row
                                    v-show="when_to_send === 'future'"
                                    data-validation-target="when_to_send"
                                >
                                    <v-col cols="auto">
                                        <v-text-field
                                            ref="scheduled_date_input"
                                            v-model="scheduled_date"
                                            label="Date"
                                            type="date"
                                            :min="min_date"
                                            :disabled="is_scheduled_but_unsent"
                                            class="mb-1"
                                            dense
                                            hide-details
                                        />
                                    </v-col>
                                    <v-col cols="auto">
                                        <v-text-field
                                            v-model="scheduled_time"
                                            :disabled="is_scheduled_but_unsent"
                                            :label="`Time (${$rc.$state.getPrimaryTimezone()})`"
                                            type="time"
                                            class="mb-1"
                                            style="min-width: 180px;"
                                            dense
                                            hide-details
                                        />
                                    </v-col>
                                </v-row>
                            </transition>
                        </v-col>
                    </v-row>
                </div>
    
                <transition name="fade">
                    <v-row
                        v-if="shouldDisable() && reasonsPreventingNextStep().length > 0"
                        class="mt-4"
                    >
                        <v-col cols="12">
                            <step-validation :reasons="reasonsPreventingNextStep()" />
                        </v-col>
                    </v-row>
                </transition>

                <transition name="fade">
                    <temp-disable-wineries-from-scheduling v-if="is_temporarily_disabled_from_scheduling">
                        <template #title>
                            <strong>While you can still send now,</strong> we have temporarily disabled scheduling messages to send in the future. Why? We're seeing unusual carrier blocking activity and want to ensure you have the most up-to-date information before your message sends, as this develops.
                        </template>
                    </temp-disable-wineries-from-scheduling>
                </transition>
    
                <v-row class="justify-center mt-6 mb-10">
                    <v-col
                        v-if="!is_scheduled_but_unsent"
                        cols="12"
                        sm="6"
                        md="4"
                    >
                        <v-btn
                            data-validation-target="edit_message"
                            color="primary"
                            outlined
                            block
                            large
                            @click="onBackToEdit('Edit Message Settings')"
                        >
                            <v-icon left>mdi-lead-pencil</v-icon>
                            Edit Message Settings
                        </v-btn>
                    </v-col>
    
                    <v-col
                        cols="12"
                        sm="6"
                        md="4"
                    >
                        <v-btn
                            :disabled="shouldDisable()"
                            color="primary"
                            block
                            large
                            @click="cta_action"
                        >
                            <v-icon left>
                                {{ cta_icon }}
                            </v-icon>
                            {{ cta_text }}
                        </v-btn>
                    </v-col>
                </v-row>
            </template>
        </template>
        <template v-else>
            <v-alert
                type="info"
                outlined
                dense
                class="mt-6 mb-10"
            >
                The rest of the review step will appear here once the recipients have been prepared.
            </v-alert>
        </template>

        <!-- Dialogs -->
        <rc-info-dialog
            v-if="$rc.isBetaFeatureNew('beta-bulk-message-review-step')"
            v-model="show_explainer_dialog"
            :persistent="false"
            icon=""
        >
            <template #title>
                <strong>Review Your Message Before Sending</strong>
            </template>

            <template #default>
                <div class="text-body-1 mb-4">
                    You're in the new <strong>Review step</strong>! Here you can <strong>preview your message</strong>, see <strong>who will receive it</strong>, and understand <strong>who's filtered out</strong>.
                    Your message won't send yet—scroll down to set the filters that are calculated at send time, then <strong>send now</strong> or <strong>schedule for later</strong>.
                </div>

                <div class="text-body-1">
                    <rc-link href="https://redchirp.com/new-bulk-message-review-step-see-who-youll-reach-before-you-send/">
                        View the tutorial & video
                    </rc-link> to learn more about the updated workflow.
                </div>
            </template>

            <template #actions>
                <v-spacer />

                <v-btn
                    color="primary"
                    @click="doCloseExplainerDialog"
                >
                    Got It!
                </v-btn>
            </template>
        </rc-info-dialog>

        <m-edit-wall-time-dialog
            ref="edit-simple-send-after-dialog"
            :title="`${bulk_message.filter_profile.filter_simple_do_not_send_after_wall_time.label} ${$rc.$state.getPrimaryTimezone()}`"
            :value="bulk_message.filter_simple_do_not_send_after_wall_time"
            :optional="false"
            use_native
            @cancel="onCancelEditSimpleDoNotSendAfter"
            @ok="onSetSimpleDoNotSendAfterFilter"
        />
    </div>
</template>

<script>
import _ from 'lodash';
import { DateTime } from 'luxon';
import { MESSAGE_STATES, RECIPIENT_STATES } from '@/js/bulk/bulk-states.js';
import MessagePatternsReadonly from '@/components/bulk/message-patterns-readonly.vue';
import RecipientsTracker from "@/components/bulk/recipients-tracker.vue";
import RecipientsTableReview from '@/components/bulk/recipients-table-review.vue';
import StepValidation from '../step-validation.vue';
import TempDisableWineriesFromScheduling from '@/components/bulk/temp-disable-wineries-from-scheduling.vue';

export default {
    name: 'BulkMessageReview',

    components: {
        MessagePatternsReadonly,
        RecipientsTracker,
        RecipientsTableReview,
        StepValidation,
        TempDisableWineriesFromScheduling,
    },

    props: {
        organization_id: {
            type: String,
            required: true,
        },
        bulk_message: {
            type: Object,
            required: false,
            default: null
        },
        bulk_message_id: {
            type: String,
            required: true,
        },
        message_state: {
            type: String,
            required: true,
        },
        message_type: {
            type: String,
            required: true,
        },
        message_type_force_mms: {
            type: Boolean,
            required: false,
            default: false
        },
        has_sent: {
            type: Boolean,
            required: true,
        },
        is_archived: {
            type: Boolean,
            default: false,
        },
        is_scheduled_but_unsent: {
            type: Boolean,
            required: false,
            default: false
        },
        loading_recipients: {
            type: Boolean,
            required: false,
            default: false,
        },
        progress_detail: {
            type: Object,
            required: false
        },
        num_recipients_under_current_filter: {
            type: Number,
            required: true,
        },
        selected_recipient_filter: {
            type: String,
            required: false,
            default: RECIPIENT_STATES.READY_TO_SEND,
        },
        recipients: {
            type: Array,
            required: true,
        },
        recipient_extra_fields: {
            type: Array,
            required: false,
            default: () => [],
        },
        full_text: {
            type: String,
            required: false,
            default: '',
        },
        table_options: {
            type: Object,
            required: true,
        },
    },

    data() {
        const scheduled_date = this.is_scheduled_but_unsent && this.bulk_message.scheduled_send_time
            ? DateTime.fromMillis(this.bulk_message.scheduled_send_time, { zone: this.$rc.$state.getPrimaryTimezone() }).toFormat('yyyy-MM-dd')
            : null;

        const scheduled_time = this.is_scheduled_but_unsent && this.bulk_message.scheduled_send_time
            ? DateTime.fromMillis(this.bulk_message.scheduled_send_time, { zone: this.$rc.$state.getPrimaryTimezone() }).toFormat('HH:mm')
            : null;

        return {
            MESSAGE_STATES,

            show_explainer_dialog: false,
            wall_time_switch_key: 0,

            consent_acknowledgement: false,
            tested_message: false,

            scheduled_date,
            scheduled_time,
            when_to_send: this.is_scheduled_but_unsent ? 'future' : 'now',
            when_to_send_options: [
                { label: 'Now', value: 'now' },
                { label: 'Some time in the future', value: 'future' }
            ],
        };
    },

    mounted() {
        const explainer_shown = localStorage.getItem('rc-bulk-message-review-step-explainer-shown');
        if (explainer_shown !== 'true') {
            this.doShowExplainerDialog();
        }
    },

    computed: {
        // temp disabling scheduling messages for wineries
        is_temporarily_disabled_from_scheduling() {
            return this.when_to_send === 'future' && this.$rc.inIndustry('wine');
        },

        local_table_options: {
            get() {
                return this.table_options;
            },
            set(value) {
                this.$emit('update:table_options', value);
            }
        },

        scheduled_send_time_display() {
            if (!this.bulk_message?.scheduled_send_time) return null;
            const dt = DateTime.fromMillis(
                this.bulk_message.scheduled_send_time,
                { zone: this.$rc.$state.getPrimaryTimezone() }
            );
            return {
                date: dt.toFormat('MMMM d, yyyy'),
                time: `${dt.toFormat('h:mm a')} (${this.$rc.$state.getPrimaryTimezone()})`,
            };
        },

        filter_simple_do_not_send_after_wall_time_boolean() {
            return this.bulk_message?.filter_profile?.filter_simple_do_not_send_after_wall_time.value != null;
        },

        filter_simple_do_not_send_after_wall_time_display_value() {
            return this.bulk_message.filter_profile.filter_simple_do_not_send_after_wall_time.value
                ? this.bulk_message.filter_profile.filter_simple_do_not_send_after_wall_time.value_description
                : 'Not Set'
        },

        filter_send_time_boolean() {
            return this.bulk_message?.filter_send_time && this.bulk_message?.filter_send_time !== 'none';
        },

        scheduled_datetime_timestamp() {
            if (!this.scheduled_date || !this.scheduled_time) return null;

            const dpart = this.scheduled_date.split("-");
            const tpart = this.scheduled_time.split(":");

            const obj = {
                year: parseInt(dpart[0]),
                month: parseInt(dpart[1]),
                day: parseInt(dpart[2]),
                hour: parseInt(tpart[0]),
                minute: parseInt(tpart[1]),
            };

            const timezone = this.$rc.$state.getPrimaryTimezone();

            // 'beng' was in place for years
            // doesn't seem to matter much since we're just getting millis, but leaving it to avoid unexpected changes
            const dt = DateTime.fromObject(obj, { zone: timezone, numberingSystem: 'beng' });

            return dt.toMillis();
        },

        min_date() {
            const today = new Date();
            return today.toISOString().split('T')[0];
        },

        cta_icon() {
            if (this.is_scheduled_but_unsent) {
                return 'mdi-cancel';
            }
            if (this.when_to_send === 'now') {
                return 'mdi-send';
            }
            if (this.when_to_send === 'future') {
                return 'mdi-calendar-clock';
            }
            return 'mdi-send';
        },

        cta_text() {
            if (this.is_scheduled_but_unsent) {
                return 'Cancel Scheduled Send';
            }
            if (this.when_to_send === 'now') {
                return 'Send Bulk Message Now';
            }
            if (this.when_to_send === 'future') {
                return 'Schedule Message';
            }
            return 'Send or Schedule';
        },

        cta_action() {
            if (this.is_scheduled_but_unsent) {
                return this.onClickCancelScheduledSend;
            }
            if (this.when_to_send === 'future') {
                return this.onScheduleTime;
            }
            return this.onClickSendBulkMessage;
        },

        ready_to_send_count() {
            return this.progress_detail?.count_in_state?.find(
                (item) => item.state === RECIPIENT_STATES.READY_TO_SEND
            )?.count_in_state || 0;
        },
    },

    methods: {
        onBackToEdit(title = 'Edit Message Settings') {
            this.$rc.confirm({
                title,
                body: 'This will take you back to the edit step. You will then need to re-initiate the review step with your updated message settings. Do you want to continue?',
                btn_text_ok: title,
                btn_text_cancel: 'Stay Here',
                on_ok: () => {
                    this.$emit('cancel-review');
                },
            });
        },

        doShowExplainerDialog() {
            this.show_explainer_dialog = true;
            localStorage.setItem('rc-bulk-message-review-step-explainer-shown', 'true');
        },

        doCloseExplainerDialog() {
            this.show_explainer_dialog = false;
        },

        onSwitchSimpleDoNotSendAfterWallTime(just_turned_on) {
            if (just_turned_on) {
                // if no existing value, prompt user to set one, otherwise just emit existing value
                if (!this.bulk_message.filter_profile.filter_simple_do_not_send_after_wall_time.value) {
                    this.onEditFilterSimpleDoNotSendAfter();
                } else {
                    const new_time = this.bulk_message.filter_profile.filter_simple_do_not_send_after_wall_time.value;
                    this.$emit('patch',
                        { filter_simple_do_not_send_after_wall_time: new_time },
                    );
                }
            } else {
                this.$emit('patch',
                    { filter_simple_do_not_send_after_wall_time: null },
                );
            }
        },

        onEditFilterSimpleDoNotSendAfter() {
            this.$refs['edit-simple-send-after-dialog'].showDialog();
        },

        onSetSimpleDoNotSendAfterFilter(value) {
            this.$emit(
                'patch',
                {
                    filter_simple_do_not_send_after_wall_time: value,
                },
            );
        },

        onCancelEditSimpleDoNotSendAfter() {
            this.wall_time_switch_key++;
        },

        onSendTimeFilterEnabledChange(enabled) {
            if (enabled) {
                // when enabling, set to the first available option (not 'none')
                const options = this.$rc.enumValues('send_time_filter_type', false);
                const first_option = options.length > 0 ? options[0].value : null;
                this.$emit('patch', { filter_send_time: first_option });
            } else {
                this.$emit('patch', { filter_send_time: 'none' });
            }
        },

        onSendTimeFilterChange(value) {
            if (value === 'none') value = null;
            this.$emit('patch', { filter_send_time: value });
        },

        onSetUsersToNotify(new_value) {
            this.$emit('patch', { notification_employee_ids: new_value });
        },

        shouldDisable() {
            if (this.is_scheduled_but_unsent) return false;

            if (this.is_temporarily_disabled_from_scheduling) {
                return true;
            }

            return this.reasonsPreventingNextStep().length > 0;
        },

        reasonsPreventingNextStep() {
            const ret = [];

            if (this.$rc.$state.profile?.provision_info?.bulk_message_send_disabled_message != null) {
                ret.push(
                    {
                        text: 'Sending bulk messages has been disabled for your organization',
                        help_text: this.$rc.$state.profile?.provision_info?.bulk_message_send_disabled_message
                    }
                );
            }

            if (this.$rc.$state.profile.employee_account.bulk_message_permissions != 'send-bulk-messages') {
                ret.push(
                    {
                        text: 'Insufficient permissions to send bulk message',
                        help_text: 'To enable sending bulk messages, ask an admin of your organization to update your user permissions. These can be changed in Organization Settings > RedChirp Users > Action > Set Bulk Message Permissions.'
                    }
                );
            }

            if (this.ready_to_send_count < 1) {
                ret.push({ text: `Need to have at least one recipient ready to send. To add recipients, edit your message settings`, target: 'edit_message' });
            }

            if (!this.consent_acknowledgement) {
                ret.push({ text: 'Need to check the affirmation check box', target: 'attestation' });
            }

            if (!this.tested_message) {
                ret.push({ text: 'Need to confirm that you have tested the message', target: 'tested' });
            }

            if (this.when_to_send === 'future') {
                if (!this.scheduled_date || !this.scheduled_time) {
                    ret.push({ text: 'Need to select a date and time for scheduling', target: 'when_to_send' });
                } else if (this.scheduled_datetime_timestamp < Date.now()) {
                    ret.push({ text: 'You can not schedule a send time to be in the past', target: 'when_to_send' });
                }
            }

            return ret;
        },

        onScheduleTime() {
            this.$rc.rest({
                spinner: false,
                endpoint: 'PUT: /api/v1/bulk/schedule-warnings',

                params: {
                    bulk_message_id: this.bulk_message_id,
                    time: this.scheduled_datetime_timestamp,
                },

                on_success: (data) => {
                    const specimen = this.$refs['bulk_message_attestation'].getSpecimen(); // grab this before DOM updates due to patch

                    if (data.warning == null) {
                        this.onScheduleTimeFrontEndWarnings(specimen);
                        return;
                    }

                    this.$rc.alert(
                        data.warning.title,
                        data.warning.body_html,
                        () => { }
                    );
                },

                on_error: 'Unable to check for scheduling time warnings',
            });
        },

        onScheduleTimeFrontEndWarnings(specimen) {
            const scheduled_datetime_timestamp = this.scheduled_datetime_timestamp;

            if (scheduled_datetime_timestamp < Date.now()) {
                this.$rc.snackbar("You can not schedule a send time to be in the past");
                this.$emit('patch', { scheduled_send_time: null });
                return;
            }

            const dt = DateTime.fromMillis(
                scheduled_datetime_timestamp,
                { zone: this.$rc.$state.getPrimaryTimezone() }
            );
            const hour = dt.hour;

            if (hour < 6 || hour > 18) {
                this.$rc.confirm(
                    'Unusual scheduled time',
                    'You are scheduling this message to go out earlier than 6:00 AM or after 7:00 PM. Are you sure?',
                    [
                        { text: 'Cancel', value: 'cancel' },
                        { text: 'Send at this time', value: 'confirm' },
                    ],

                    (response) => {
                        if (response !== 'confirm') return;

                        this.$emit('patch', {
                            scheduled_send_time: scheduled_datetime_timestamp,
                            scheduled_by_employee_id: this.$rc.$state.getEmployeeId()
                        }, {
                            on_success: () => {
                                this.$emit('schedule-bulk-message', specimen);
                            },
                        });
                    }
                );
            } else {
                this.$emit('patch', {
                    scheduled_send_time: scheduled_datetime_timestamp,
                    scheduled_by_employee_id: this.$rc.$state.getEmployeeId()
                }, {
                    on_success: () => {
                        this.$emit('schedule-bulk-message', specimen);
                    },
                });
            }
        },

        onClickSendBulkMessage() {
            this.$rc.rest({
                spinner: false,
                endpoint: 'PUT: /api/v1/bulk/schedule-warnings',

                params: {
                    bulk_message_id: this.bulk_message_id,
                    time: null, // meaning, now
                },

                on_success: (data) => {
                    if (data.warning == null) {
                        this.afterWarningsOnClickSendBulkMessage();
                        return;
                    }
                    this.$rc.alert(
                        data.warning.title,
                        data.warning.body_html,
                        () => { }
                    );
                },

                on_error: 'Unable to check for scheduling time warnings',
            });
        },

        afterWarningsOnClickSendBulkMessage() {
            this.$rc.confirm(
                'Final Confirmation: Send message',
                'This is a final confirmation that you are ready to send this message to <b>' + this.ready_to_send_count.toLocaleString() + ' recipients.</b><br/><br/>Once you press send <b>RedChirp will start sending this message to the recipients you have specified.</b>',
                [
                    { text: 'Cancel', value: 'cancel' },
                    { text: 'Send', value: 'confirm' },
                ],
                (response) => {
                    if (response !== 'confirm') return;
                    this.$emit('send-bulk-message', this.$refs['bulk_message_attestation'].getSpecimen());
                },
            );
        },

        onClickCancelScheduledSend() {
            this.$rc.confirm({
                title: 'Cancel Scheduled Send',
                body: 'Are you sure that you would like to cancel the scheduled send of this bulk message?',
                btn_text_ok: 'Cancel scheduled send',
                btn_text_cancel: 'No',
                on_ok: () => {
                    this.consent_acknowledgement = false;
                    this.tested_message = false;
                    this.$emit('cancel-scheduled-send');
                },
            });
        },
    },

    watch: {
        when_to_send(new_val) {
            if (new_val === 'future') {
                this.$nextTick(() => {
                    this.$refs.scheduled_date_input?.focus();
                });
            }
        },
    },
}
</script>

<style lang="scss" scoped>
:deep(.v-input--switch .v-input__slot) {
    column-gap: .5rem;
}

h3,
:deep(h3) {
    font-size: 1rem;
}

:deep(.form-select-button) {
    padding-top: 0;
    padding-bottom: 0;
}
</style>